import { Component, inject, OnInit } from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { throwError } from 'rxjs';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { CheckboxComponent } from '@atoms/checkbox/checkbox.component';
import { FormComponent } from '@atoms/form/form.component';
import { FormInputsComponent } from '@atoms/form/form-inputs/form-inputs.component';
import { FormSectionComponent } from '@atoms/form/form-section/form-section.component';
import { FormTitleComponent } from '@atoms/form/form-title/form-title.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { InputComponent } from '@atoms/input/input.component';
import { LinkComponent } from '@atoms/link/link.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { SelectComponent } from '@atoms/select/select.component';
import { SelectItemComponent } from '@atoms/select/select-item/select-item.component';
import { TextareaComponent } from '@atoms/textarea/textarea.component';
import { ContactInformationsComponent } from '@molecules/contact-informations/contact-informations.component';
import { FormThanksMessageComponent } from '@molecules/form-thanks-message/form-thanks-message.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalSidebarComponent } from '@molecules/modal/modal-sidebar/modal-sidebar.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { ComplaintService } from '@services/complaint.service';
import { GlobalsService } from '@services/globals.service';
import { ComplaintRequest } from '@models/complaint.model';
import { GtmService } from '@services/gtm.service';

declare var Cookies: any;

@Component({
  selector: 'app-complaint-modal',
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    CheckboxComponent,
    FormComponent,
    FormInputsComponent,
    FormSectionComponent,
    FormThanksMessageComponent,
    FormTitleComponent,
    GridComponent,
    InputComponent,
    LinkComponent,
    ParagraphComponent,
    PhoneInputComponent,
    SelectComponent,
    SelectItemComponent,
    TextareaComponent,
    ContactInformationsComponent,
    ModalHeaderComponent,
    ModalMainComponent,
    ModalMainContentComponent,
    ModalSidebarComponent,
    ModalTitleComponent,
  ],
  templateUrl: './complaint-modal.component.html',
  styleUrl: './complaint-modal.component.scss',
})
export class ComplaintModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  public globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  private complaintService = inject(ComplaintService);
  private gtmService = inject(GtmService);
  public form = this.fb.nonNullable.group({
    name: ['', { nonNullable: true, validators: [Validators.required] }],
    phone: ['', { nonNullable: true, validators: [Validators.required] }],
    phoneCode: ['+420', [Validators.required]],
    email: ['', { nonNullable: true, validators: [Validators.required, Validators.email] }],
    reason: ['', { nonNullable: true, validators: [Validators.required] }],
    registrationCode: ['', { nonNullable: true, validators: [Validators.required] }],
    note: [''],
    marketingAgree: [false, { nonNullable: true, validators: [] }],
  });
  private ip: string = '';
  public step = 1;

  send(): void {
    if (this.form.invalid) return;
    const formData = this.form.getRawValue();

    const statsMonitorId = Cookies.get('_stats_of_visits');
    const exponeaEtc = Cookies.get('__exponea_etc__');
    const utmSource = Cookies.get('utm_source');
    const utmMedium = Cookies.get('utm_medium');
    const utmContent = Cookies.get('utm_content');
    const utmCampaign = Cookies.get('utm_campaign');
    const utmTs = Cookies.get('utm_ts');
    const utmTerm = Cookies.get('utm_term');

    const phoneCodeNumber: number | undefined = formData.phoneCode ? Number(formData.phoneCode.replace('+', '')) : undefined;
    const phoneCountry: number | undefined = this.globalsService.searchPhoneCountryCode(phoneCodeNumber);
    const complaintRequest: ComplaintRequest = {
      complaintId: Number(formData.reason),
      name: formData.name,
      email: formData.email,
      phoneCountryId: phoneCountry!,
      phone: formData.phone,
      message: formData.note,
      countryId: 23,
      branchId: 356,
      carSPZ: formData.registrationCode,
      customerIP: this.ip,
      marketingAgree: formData.marketingAgree,
      personalAgree: true,
      cookieAgree: true,
      utmSource: utmSource ? utmSource : undefined,
      utmMedium: utmMedium ? utmMedium : undefined,
      utmCampaign: utmCampaign ? utmCampaign : undefined,
      utmContent: utmContent ? utmContent : undefined,
      utmTs: utmTs ? utmTs : undefined,
      utmTerm: utmTerm ? utmTerm : undefined,
      statsMonitorId: statsMonitorId ? statsMonitorId : undefined,
      exponeaEtc: exponeaEtc ? exponeaEtc : undefined,
      webFormUrl: 'www.mototechna.cz',
      dsData: undefined,
    };
    this.complaintService.sendForm(complaintRequest).subscribe({
      next: () => {
        this.form.reset();
        this.form.markAsUntouched();
        this.step = 2;

        const dataLayer = {
          event: 'page_view',
          pageURL: '/' + this.globals.language() + '/podekovani-reklamacni-formular',
          pageType: 'Purchase',
          pageTitle: 'Poděkování reklamační formulář',
        };
        this.gtmService.pushGtm(dataLayer); 
      },
      error: (error) => {
        throwError(() => 'Something went wrong:' + error);
      },
    });
  }

  ngOnInit(): void {
    this.complaintService.getUserIp().subscribe((response) => {
      this.ip = response.ip;
    });

    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.globals.language() + '/reklamacni-formular',
      pageType: 'Form',
      pageTitle: 'Reklamační formulář',
    };
    this.gtmService.pushGtm(dataLayer); 
  }
}
